<template>
    <div class="stacked-bar-graph">
        <!-- 라인 + 라벨텍스트 -->
        <div
            class="border-line"
            v-for="(item, index) in props.data.BorderlineInfo?.Borderline"
            :key="item.DisplayValue ?? index"
            :style="computedBorderLineStyle(item)"
        >
            <p
                class="border-line-label"
                :class="computedBorderLineLabelClass(item)"
                :style="getBorderLineLabelStyle(item, index)"
                v-show="item.LineName && item.LineName.length > 0"
            >
                {{ formatLineName(item.LineName ?? '') }}
            </p>
        </div>
        <div class="stacked-bar-list-container">
            <!-- 배경만 -->
            <div
                class="border-box"
                :style="computedBorderBoxStyle"
                v-if="props.data.BorderlineInfo?.Borderline?.length === 2"
            ></div>
            <div class="stacked-bar-list-wrap">
                <ul class="stacked-bar-list">
                    <li v-for="(item, index) in props.data.BarList" :key="index">
                        <div class="stacked-bar">
                            <!-- 최대 2개 -->
                            <div
                                class="stacked-bar-inner"
                                v-for="(segment, itemIndex) in item.SegmentList"
                                :key="itemIndex"
                                :style="computedSegmentStyle(segment)"
                                :class="{
                                    'has-bar-gap': segment.BarGap && segment.BarGap > 0,
                                }"
                            >
                                <span
                                    class="bar-label"
                                    :class="{
                                        start: index === 0,
                                        end: index === (props.data?.BarList?.length ?? 0) - 1,
                                    }"
                                    :style="{
                                        color: item?.SegmentList?.[itemIndex]?.Color ?? '',
                                    }"
                                    v-if="(item?.SegmentList?.[itemIndex]?.DisplayValue ?? 0) > 0"
                                >
                                    <b>{{
                                        item?.SegmentList?.[itemIndex]?.DisplayValue != null
                                            ? formatNumberToDecimal(item.SegmentList[itemIndex].DisplayValue)
                                            : ''
                                    }}</b>
                                    <span>{{ `${computedUnitString}` }}</span>
                                </span>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>

            <ul class="weekday-list">
                <li v-for="(item, index) in props.data.BarList" :key="index">
                    {{ item.Label }}
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, nextTick } from 'vue';
import type { StackedBarBarSegment } from '@common/openapi-client/openapi';
// import { referenceModuleprops.data2 } from '@/const/referenceModuleprops.data';
import { getBodyCompInfoValue, formatNumberToDecimal } from '@common/common/utils/graphUtils';
import { REFERENCE_GRAPH_BACKGROUND } from '@common/trend-report-common';
import { hexToRgba } from '@common/common/utils/graphUtils';
import type { BorderlineSegment, BorderlineBarGraph } from '@common/openapi-client/openapi';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// 데이터 연결 후 props 데이터로 추가
// const props.data = referenceModuleprops.data2;

const props = defineProps<{
    data: BorderlineBarGraph;
    index: number;
    bodyComp: string;
}>();

const BAR_AREA_HEIGHT = 96; //.stacked-bar-list 높이(px)와 동일하게 설정
// const BORDER_LINE_PADDING = 2; // 위아래 2px씩

const labelLineCounts = ref<number[]>([]); // index별 줄 수 저장

// const computedBorderline = computed(() => {
//     // TODO: 수정필요
//     // 라인 컬러
//     // 라인 스타일
//     // 텍스트
//     const borderlineList = props.data?.BorderlineInfo?.Borderline ?? [];

//     if (borderlineList.length === 1) {
//         return (1 - (borderlineList[0]?.NormalizedValue ?? 0)) * 100;
//     }

//     if (borderlineList.length === 2) {
//         const [a, _] = borderlineList
//             .map((d: BorderlineSegment) => d?.NormalizedValue ?? 0)
//             .sort((x: number, y: number) => y - x);
//         return (1 - a) * 100;
//     }

//     return 0;
// });

/** 글자 수·공백 수에 따라 줄바꿈 위치를 결정한다. */
const formatLineName = (name: string | undefined): string => {
    name = name?.trim();
    if (!name || name.length < 6) return name ?? '';

    const spaceMatches = [...name.matchAll(/\s/g)];
    const spaceIdx = spaceMatches.map(m => m.index!);
    if (spaceIdx.length === 0) return name;

    // 줄바꿈 삽입 조건: 전체 글자 수 6자 이상인 경우에만
    if (name.length >= 7) {
        let breakPos = spaceIdx[0]; // 공백 1개일 때는 첫 번째 공백
        if (spaceIdx.length >= 3) breakPos = spaceIdx[1]; // 공백 3개 이상일 땐 두 번째

        return name.slice(0, breakPos) + '\n' + name.slice(breakPos + 1);
    }

    return name;
};

const changeLineColor = (lineColorCode: string) => {
    return REFERENCE_GRAPH_BACKGROUND.find(d => d.name === lineColorCode)?.lineColor ?? '';
};

const computedBorderLineStyle = (item: BorderlineSegment) => {
    const normalized = item?.NormalizedValue ?? 0;
    const topPx = (1 - normalized) * BAR_AREA_HEIGHT;

    return {
        top: `${topPx}px`,
        border: item.LineStyle
            ? `0.5px ${
                  item.LineStyle === 'dash' ? 'dashed' : 'solid'
              } ${item.LineColor === 'transparent' ? 'transparent' : item.LineColor === '' ? 'transparent' : item.LineColor}`
            : '',
    };
};

// const computedBorderLineLabelClass = (item: BorderlineSegment) => {
//     const borderlineList = props.data?.BorderlineInfo?.Borderline ?? [];

//     if (borderlineList.length !== 2) return 'top'; // 기본값

//     const [itemA, itemB] = borderlineList;

//     const topItem = (itemA.NormalizedValue ?? 0) > (itemB.NormalizedValue ?? 0) ? itemA : itemB;
//     return item === topItem ? 'top' : 'bottom';
// };

const computedBorderLineLabelClass = (item: BorderlineSegment) => {
    const borderlineList = props.data?.BorderlineInfo?.Borderline ?? [];
    if (borderlineList.length !== 2) return 'top';

    const [itemA, itemB] = borderlineList;
    const nameA = (itemA.LineName ?? '').trim();
    const nameB = (itemB.LineName ?? '').trim();

    // ---------- 우선 규칙 ----------
    // 둘 다 '하위'면 둘 다 bottom
    if (nameA.includes('하위') && nameB.includes('하위')) return 'bottom';
    // 둘 다 '상위'면 둘 다 top
    if (nameA.includes('상위') && nameB.includes('상위')) return 'top';
    // 하나라도 '표준 이하'가 있으면 둘 다 bottom (요구사항)
    if (/이하/.test(nameA) || /이하/.test(nameB)) return 'bottom';
    // 하나라도 '표준 이상'이 있으면 둘 다 top (요구사항)
    if (/이상/.test(nameA) || /이상/.test(nameB)) return 'top';

    // ---------- 개별 판정 함수 (먼저 오는 것 우선) ----------
    const decide = (name: string): 'top' | 'bottom' | null => {
        if (/표준\s*이하/.test(name)) return 'bottom';
        if (/표준\s*이상/.test(name)) return 'top';
        if (/(이하|하위)/.test(name)) return 'bottom';
        if (/(이상|상위)/.test(name)) return 'top';
        // 표준 단독 기본값: bottom (필요시 'top'으로 바꿔도 됨)
        if (/표준/.test(name)) return 'bottom';
        return null;
    };

    // 현재 아이템 기준으로 먼저 결정
    const current = decide(item.LineName ?? '');
    if (current) return current;

    // 다 안 걸리면 기존 높이 비교 fallback
    const topItem = (itemA.NormalizedValue ?? 0) > (itemB.NormalizedValue ?? 0) ? itemA : itemB;
    return item === topItem ? 'top' : 'bottom';
};

const getBorderLineLabelStyle = (item: BorderlineSegment, index: number) => {
    const lineCount = labelLineCounts.value[index] ?? 1;
    const position = computedBorderLineLabelClass(item);

    const offsetRem = `${lineCount * 1.1}rem`;

    return {
        color:
            item.LineName?.includes(t('trend_report_reference_line_text_bottom_area')) ||
            item.LineName?.includes(t('trend_report_reference_line_text_top_area'))
                ? '#606976'
                : changeLineColor(item.LineColor ?? ''),
        fontWeight:
            (props.data.BorderlineInfo?.Borderline?.length ?? 0) > 1
                ? item.LineStyle === 'dash'
                    ? '400'
                    : '700'
                : '700',
        [position]: `-${offsetRem}`,
    };
};

const computedBorderBoxStyle = computed(() => {
    const borderlineList = props.data?.BorderlineInfo?.Borderline ?? [];

    if (borderlineList.length !== 2) return {};

    const [a, b] = borderlineList.map(d => d.NormalizedValue ?? 0).sort((x, y) => y - x); // a > b

    const topPx = (1 - a) * BAR_AREA_HEIGHT;
    const heightPx = (a - b) * BAR_AREA_HEIGHT;

    let background = '';
    const fillColor = props.data.BorderlineInfo?.FillColor;
    if (fillColor && fillColor.length > 0) {
        const gradientColorList = REFERENCE_GRAPH_BACKGROUND.find(d => d.name === fillColor)?.gradientColors;

        if (gradientColorList) {
            const reversedColors = [...gradientColorList].reverse();
            const gradient =
                reversedColors.length === 3
                    ? [
                          `${hexToRgba(reversedColors[0], 0.2)} 0%`,
                          `${hexToRgba(reversedColors[1], 0.2)} 50%`,
                          `${hexToRgba(reversedColors[2], 0.2)} 80%`,
                      ]
                    : reversedColors.length === 2
                      ? [`${hexToRgba(reversedColors[0], 0.2)} 0%`, `${hexToRgba(reversedColors[1], 0.2)} 80%`]
                      : reversedColors.map((c, i) => `${hexToRgba(c, 0.2)} ${i * 100}%`);

            background = `radial-gradient(${gradient.join(', ')})`;
        }
    }

    return {
        top: `${topPx}px`,
        height: `${heightPx}px`,
        background,
    };
});

const computedUnitString = computed(() => {
    return getBodyCompInfoValue(props.bodyComp, 'unit') ?? '';
});

const computedSegmentStyle = (segment: StackedBarBarSegment) => {
    if (!segment.NormalizedValue || segment.NormalizedValue === 0) {
        return {
            display: 'none',
        };
    }

    return {
        height: (segment.NormalizedValue ?? 0) * 100 + '%',
        backgroundColor: segment.Color ?? '',
        borderRadius: segment.Rvalue && segment.Rvalue > 0 ? `${segment.Rvalue}px ${segment.Rvalue}px 0 0` : '',
        // marginTop:
        //     segment.BarGap && segment.BarGap > 0 ? `${segment.BarGap}px` : '0',
        opacity: segment.Alpha && segment.Alpha > 0 ? segment.Alpha : 1,
        border: segment.BarStyle === 'dash' ? `1px dashed ${segment.Color}` : `1px solid ${segment.Color}`,
        '--bar-gap-height': `${segment.BarGap ?? 0}px`,
    };
};

onMounted(() => {
    nextTick(() => {
        // 보더라인 라벨 높이 계산
        const labelEls = document.querySelectorAll(`.stacked-bar-graph .border-line-label`);

        labelLineCounts.value = Array.from(labelEls).map(el => {
            const lineHeight = parseFloat(getComputedStyle(el).lineHeight || '16'); // fallback 16px
            const height = el.getBoundingClientRect().height;
            return Math.round(height / lineHeight);
        });
    });
});
</script>

<style scoped lang="scss">
.stacked-bar-graph {
    position: relative;
    // padding-top: 1.8rem;

    & + .stacked-bar-graph {
        margin-top: 1.8rem;
    }
}

.stacked-bar-list-container {
    width: 78%;
    position: relative;

    .border-line-title {
        position: absolute;
        left: 0;
        width: 100%;
        z-index: 11;

        &.static {
            position: static;
        }
    }
}

.stacked-bar-list-wrap {
    width: 100%;
    border-bottom: 1px solid $grey-2;
}

.stacked-bar-list {
    width: 96%;
    height: 9.6rem;
    // padding: 0 1rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    li {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;

        .stacked-bar {
            width: 70%;
            // padding: 0 0.3rem;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;

            div {
                &.stacked-bar-top {
                    position: relative;
                    border-bottom: 1px solid $primary-white;
                    box-sizing: border-box;
                    border-radius: 0.1rem 0.1rem 0 0;

                    .bar-label {
                        position: absolute;
                        top: -1.4rem;
                        left: 50%;
                        transform: translateX(-50%) scale(0.8);
                        line-height: 150%;
                        font-size: 1rem;
                        font-weight: 400;
                        white-space: nowrap;
                    }
                }
            }
        }
    }
}

.border-line {
    position: absolute;
    left: 0;
    width: 100%;
    z-index: 10;

    .border-line-label {
        position: absolute;
        text-align: right;
        line-height: 100%;
        font-weight: 700;
        transform: scale(0.8);
        transform-origin: right;
        right: 0;
        white-space: pre-line;
        font-size: 1rem;

        &.top {
            top: -1.1rem;
        }

        &.bottom {
            bottom: -1.1rem;
        }
    }
}

.border-box {
    position: absolute;
    left: 0;
    width: 100%;
    z-index: 1;
    box-sizing: border-box;
    z-index: 0;
}

.weekday-list {
    width: 96%;
    // height: 2.4rem;
    // padding: 0 1rem;
    padding-top: 0.4rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    font-size: 1rem;

    li {
        width: 100%;
        height: 100%;
        // display: flex;
        // align-items: flex-end;
        // justify-content: center;
        background-color: #fff;
        color: $grey-5;
        font-size: 1rem;
        // transform: scale(0.9);
        white-space: nowrap;
    }
}

.stacked-bar-inner {
    position: relative;
    box-sizing: border-box;

    .bar-label {
        display: flex;
        position: absolute;
        top: -1.2rem;
        left: 50%;
        font-size: 1rem;
        line-height: 100%;
        transform: translateX(-50%) scale(0.8);
        z-index: 1;
    }

    &.has-bar-gap::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: var(--bar-gap-height, 1px); // 기본값 4px
        background-color: #fff;
    }
}
</style>
